/**
 * Potrącenia z wynagrodzenia — tytuły wykonawcze, zaliczki i kary
 * (Etap 33, ADR-0034).
 *
 * ADR-0010 wypisał granice silnika płac: „czego silnik nie liczy, tego nie
 * udaje". Chorobowe zniknęło z tej listy w Etapie 11; potrącenia komornicze
 * zostały ostatnie. Firma, do której trafi jedno zajęcie wynagrodzenia, nie
 * mogła u nas policzyć listy płac zgodnie z prawem.
 *
 * ## Co wolno potrącić i w jakiej kolejności (art. 87 § 1 k.p.)
 *
 *  1. sumy egzekwowane na mocy tytułów wykonawczych na zaspokojenie świadczeń
 *     **alimentacyjnych**,
 *  2. sumy egzekwowane na mocy tytułów wykonawczych na pokrycie należności
 *     **innych** niż alimentacyjne,
 *  3. **zaliczki pieniężne** udzielone pracownikowi,
 *  4. **kary pieniężne** przewidziane w art. 108 k.p.
 *
 * Kolejność nie jest kwestią gustu: przy zbiegu tytułów alimenty zaspokaja się
 * pierwsze, a dopiero z tego, co zostanie w granicach, kolejne pozycje.
 *
 * ## Granice potrąceń (art. 87 § 3 i § 4 k.p.)
 *
 *  - alimenty — do **3/5** wynagrodzenia,
 *  - inne należności i zaliczki — do **1/2**,
 *  - inne razem z zaliczkami nie mogą przekroczyć 1/2, a wszystko razem
 *    z alimentami **3/5**.
 *
 * ## Kwota wolna od potrąceń (art. 87(1) § 1 i § 2 k.p.)
 *
 *  - inne należności — **100 %** minimalnego wynagrodzenia (netto),
 *  - zaliczki pieniężne — **75 %** tej kwoty,
 *  - kary pieniężne — **90 %**,
 *  - **przy alimentach kwoty wolnej NIE MA** — i to jest reguła, którą przy
 *    ręcznym liczeniu najłatwiej pomylić,
 *  - przy niepełnym wymiarze czasu pracy kwoty wolne zmniejszają się
 *    proporcjonalnie (§ 2).
 *
 * ## Zasiłki mają WŁASNY reżim (Etap 36, ADR-0037)
 *
 * Kodeks pracy rządzi wynagrodzeniem za pracę — w tym wynagrodzeniem
 * chorobowym z art. 92 k.p., które pracodawca finansuje sam i które zasiłkiem
 * nie jest. Zasiłki (chorobowy, opiekuńczy, macierzyński) to świadczenia
 * z ubezpieczenia społecznego: art. 833 § 5 k.p.c. odsyła przy nich do ustawy
 * o emeryturach i rentach z FUS, a ta ma inne granice, inne kwoty wolne i inną
 * podstawę liczenia:
 *
 *  - granice z art. 140 ust. 1: alimenty do **60 %**, inne należności
 *    egzekwowane do **25 %**, a wszystko razem do 60 % (ust. 4 pkt 2),
 *  - liczone od kwoty świadczenia **BRUTTO** — art. 140 ust. 7 mówi wprost:
 *    przed odliczeniem podatku i składki zdrowotnej,
 *  - kwoty wolne z art. 141 są KWOTAMI ZŁOTOWYMI ogłaszanymi co roku (komunikat
 *    Prezesa ZUS), waloryzowanymi od 1 marca — nie procentem minimalnej płacy;
 *    sprawdza się je wobec świadczenia po zaliczce na podatek (art. 141
 *    ust. 1b), a przy okresie krótszym niż miesiąc dzieli przez 30 i mnoży
 *    przez liczbę dni zasiłkowych,
 *  - z zasiłku wolno potrącić TYLKO należności z tytułów wykonawczych: zaliczek
 *    pieniężnych ani kar z art. 108 k.p. art. 139 ust. 1 ustawy emerytalnej nie
 *    zna, bo to instrumenty prawa pracy.
 */

/** Rodzaje potrąceń w kolejności z art. 87 § 1 k.p. */
export const GARNISHMENT_KINDS = ['alimenty', 'inne', 'zaliczka', 'kara'] as const
export type GarnishmentKind = (typeof GARNISHMENT_KINDS)[number]

/**
 * Granica potrącenia dla rodzaju, w punktach bazowych wynagrodzenia:
 * alimenty 3/5 = 6000 bp, pozostałe 1/2 = 5000 bp.
 */
export const GARNISHMENT_LIMIT_BP: Record<GarnishmentKind, number> = {
  alimenty: 6000,
  inne: 5000,
  zaliczka: 5000,
  kara: 5000,
}

/** Granica dla WSZYSTKICH potrąceń razem — 3/5 wynagrodzenia (art. 87 § 4). */
export const GARNISHMENT_TOTAL_LIMIT_BP = 6000

/** Granica dla potrąceń NIEALIMENTACYJNYCH razem — 1/2 wynagrodzenia. */
export const GARNISHMENT_NON_ALIMONY_LIMIT_BP = 5000

/**
 * Kwota wolna jako część minimalnego wynagrodzenia netto, w punktach bazowych.
 * Alimenty jej nie mają — dlatego zero, a nie brak wpisu: chcemy, żeby tablica
 * była kompletna i czytana jako reguła, nie jako przeoczenie.
 */
export const GARNISHMENT_PROTECTED_BP: Record<GarnishmentKind, number> = {
  alimenty: 0,
  inne: 10_000,
  zaliczka: 7500,
  kara: 9000,
}

/** Czy rodzaj potrącenia pochodzi z tytułu wykonawczego (egzekucja komornicza). */
export function isEnforcementTitle(kind: GarnishmentKind): boolean {
  return kind === 'alimenty' || kind === 'inne'
}

/**
 * Rodzaje z tytułu wykonawczego — do warunków w zapytaniach (#12). Kara
 * i zaliczka to potrącenia na rzecz pracodawcy: nie dotyczy ich ani wzmianka
 * o zajęciu w świadectwie pracy (art. 884 § 2 k.p.c.), ani oświadczenie
 * z art. 882 § 1, ani odbiorca przelewu.
 */
export const ENFORCEMENT_GARNISHMENT_KINDS: readonly GarnishmentKind[] =
  GARNISHMENT_KINDS.filter(isEnforcementTitle)

/**
 * Granica potrącenia z ZASIŁKU, w punktach bazowych świadczenia brutto
 * (art. 140 ust. 1 ustawy o emeryturach i rentach z FUS): alimenty 60 %, inne
 * należności egzekwowane 25 %.
 *
 * Zaliczki i kary mają zero, bo z zasiłku potrącić ich nie wolno — art. 139
 * ust. 1 ustawy emerytalnej ich nie wymienia. Zero, a nie brak wpisu: tablica
 * ma się czytać jako reguła, nie jako przeoczenie.
 */
export const BENEFIT_GARNISHMENT_LIMIT_BP: Record<GarnishmentKind, number> = {
  alimenty: 6000,
  inne: 2500,
  zaliczka: 0,
  kara: 0,
}

/** Granica dla wszystkich potrąceń z zasiłku razem — 60 % (art. 140 ust. 4 pkt 2). */
export const BENEFIT_GARNISHMENT_TOTAL_LIMIT_BP = 6000

/**
 * Dzielnik kwoty wolnej przy okresie zasiłkowym krótszym niż miesiąc: kwotę
 * miesięczną dzieli się przez 30 i mnoży przez liczbę dni zasiłkowych.
 */
export const BENEFIT_FREE_DIVISOR = 30

/** Czy z zasiłku wolno potrącić ten rodzaj należności (art. 139 ust. 1). */
export function isBenefitDeductible(kind: GarnishmentKind): boolean {
  return BENEFIT_GARNISHMENT_LIMIT_BP[kind] > 0
}
